import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Award, Trophy, Zap, Star, ChevronDown, ChevronUp, Timer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '../config/api';

const BestTimesView = ({ raceId, comparisonData, getDriverColor, race, userId }) => {
  const [bestTimes, setBestTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favoriteDrivers, setFavoriteDrivers] = useState(new Set());
  const [expandedSectors, setExpandedSectors] = useState({});

  useEffect(() => {
    loadBestTimes();
    if (userId) {
      loadFavorites();
    }
  }, [raceId, userId]);

  const loadBestTimes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/races/${raceId}/best-times`);
      setBestTimes(response.data);
      // Auto-expand all sectors by default
      const expanded = {};
      response.data.best_sectors.forEach(s => {
        expanded[s.sector_number] = true;
      });
      setExpandedSectors(expanded);
    } catch (error) {
      toast.error('Error al cargar mejores tiempos');
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites?user_id=${userId}`);
      const favNames = new Set(response.data.map(f => f.driver_name.toLowerCase()));
      setFavoriteDrivers(favNames);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const isFavorite = (driverName) => {
    return favoriteDrivers.has(driverName?.toLowerCase());
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(3);
    return mins > 0 ? `${mins}:${secs.padStart(6, '0')}` : `${secs}s`;
  };

  const formatSectorTime = (seconds) => {
    if (!seconds || seconds === 0) return '--';
    return seconds.toFixed(3);
  };

  const toggleSector = (sectorNumber) => {
    setExpandedSectors(prev => ({
      ...prev,
      [sectorNumber]: !prev[sectorNumber]
    }));
  };

  // Build ranking for each sector using all drivers from race
  const getSectorRanking = (sectorNumber) => {
    if (!race?.drivers) return [];
    
    const ranking = [];
    
    race.drivers.forEach(driver => {
      let bestSectorTime = null;
      
      if (driver.sector_times && Array.isArray(driver.sector_times)) {
        const sectorData = driver.sector_times.find(s => s.sector_number === sectorNumber);
        if (sectorData && sectorData.times && sectorData.times.length > 0) {
          const validTimes = sectorData.times.filter(t => t > 0);
          if (validTimes.length > 0) {
            bestSectorTime = Math.min(...validTimes);
          }
        }
      }
      
      if (bestSectorTime) {
        ranking.push({
          driver_number: driver.driver_number,
          driver_name: driver.driver_name,
          sector_time: bestSectorTime,
          is_favorite: isFavorite(driver.driver_name)
        });
      }
    });
    
    // Statistical outlier removal (IQR method - both low and high outliers)
    if (ranking.length > 4) {
      const sorted = [...ranking].sort((a, b) => a.sector_time - b.sector_time);
      const q1Idx = Math.floor(sorted.length * 0.25);
      const q3Idx = Math.floor(sorted.length * 0.75);
      const q1 = sorted[q1Idx].sector_time;
      const q3 = sorted[q3Idx].sector_time;
      const iqr = q3 - q1;
      const upperBound = q3 + 2.5 * iqr;
      const lowerBound = Math.max(0.5, q1 - 2.5 * iqr);
      const filtered = ranking.filter(d => d.sector_time >= lowerBound && d.sector_time <= upperBound);
      if (filtered.length >= 3) {
        ranking.length = 0;
        ranking.push(...filtered);
      }
    }
    
    ranking.sort((a, b) => a.sector_time - b.sector_time);
    
    const leaderTime = ranking[0]?.sector_time || 0;
    ranking.forEach((driver, idx) => {
      driver.position = idx + 1;
      driver.gap = idx === 0 ? 0 : driver.sector_time - leaderTime;
    });
    
    return ranking;
  };

  // Build overall fastest laps ranking with sector breakdown
  const getFastestLapsRanking = () => {
    if (!race?.drivers) return [];
    
    const ranking = race.drivers
      .filter(d => d.best_lap && d.best_lap > 0)
      .map(driver => {
        // Find best sector times for this driver
        const bestSectors = [];
        if (driver.sector_times && Array.isArray(driver.sector_times)) {
          driver.sector_times.forEach(sector => {
            const validTimes = (sector.times || []).filter(t => t > 0);
            if (validTimes.length > 0) {
              bestSectors[sector.sector_number - 1] = Math.min(...validTimes);
            }
          });
        }
        
        return {
          driver_number: driver.driver_number,
          driver_name: driver.driver_name,
          best_lap: driver.best_lap,
          best_sectors: bestSectors,
          is_favorite: isFavorite(driver.driver_name)
        };
      })
      .sort((a, b) => a.best_lap - b.best_lap);
    
    // Add position and gap
    const leaderTime = ranking[0]?.best_lap || 0;
    ranking.forEach((driver, idx) => {
      driver.position = idx + 1;
      driver.gap = idx === 0 ? 0 : driver.best_lap - leaderTime;
    });
    
    return ranking;
  };

  if (loading) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 text-center">
          <div className="w-16 h-16 mx-auto border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400">Cargando mejores tiempos...</p>
        </CardContent>
      </Card>
    );
  }

  if (!bestTimes) {
    return null;
  }

  const fastestLapsRanking = getFastestLapsRanking();
  const numSectors = bestTimes.best_sectors.length;
  // Best actual lap (already in fastestLapsRanking[0]) vs theoretical best.
  // Engineers care a LOT about this delta — that's the time left on the table.
  const fastestActual = fastestLapsRanking[0]?.best_lap || 0;
  const theoretical = bestTimes.theoretical_best_lap || 0;
  const idealGap = fastestActual && theoretical ? fastestActual - theoretical : null;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-red-950/50 to-neutral-900 border-red-600">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-2xl text-white flex items-center gap-2">
                <Award className="w-7 h-7 text-red-500" />
                Mejores Parciales de la Categoría
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Ranking completo por sector • Favoritos resaltados con ⭐
              </CardDescription>
            </div>
            <div className="flex items-center gap-6">
              {fastestActual > 0 && (
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 uppercase">Mejor vuelta real</p>
                  <p className="text-xl font-bold text-white font-mono" data-testid="fastest-actual-lap">
                    {formatTime(fastestActual)}
                  </p>
                </div>
              )}
              <div className="text-right">
                <p className="text-[11px] text-gray-400 uppercase">Vuelta teórica perfecta</p>
                <p className="text-3xl font-bold text-purple-400 font-mono" data-testid="theoretical-best-lap">
                  {formatTime(theoretical)}
                </p>
                {idealGap !== null && idealGap > 0.001 && (
                  <p className="text-xs text-yellow-400 font-mono mt-0.5" data-testid="ideal-gap">
                    Quedaron <strong>{idealGap.toFixed(3)}s</strong> sobre la mesa
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* ========== NUEVO: RANKING DE VUELTAS RÁPIDAS ========== */}
      <Card className="bg-neutral-900 border-red-600" data-testid="fastest-laps-ranking">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Timer className="w-5 h-5 text-green-500" />
            Ranking de Vueltas Rápidas
          </CardTitle>
          <CardDescription className="text-gray-500">
            Mejor vuelta de cada piloto con desglose de sectores
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-700 bg-neutral-800/50">
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">Pos</th>
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">#</th>
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">Piloto</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Mejor Vuelta</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Gap</th>
                  {Array.from({ length: numSectors }, (_, i) => (
                    <th key={i} className="text-right py-2 px-2 text-gray-400 font-medium">S{i + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fastestLapsRanking.map((driver, idx) => (
                  <tr 
                    key={driver.driver_number}
                    className={`border-b border-neutral-800 transition-colors ${
                      driver.is_favorite 
                        ? 'bg-yellow-500/10 hover:bg-yellow-500/15' 
                        : idx === 0 
                          ? 'bg-green-500/5 hover:bg-green-500/10'
                          : 'hover:bg-neutral-800/50'
                    }`}
                  >
                    {/* Position */}
                    <td className="py-2 px-3">
                      <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-500 text-black' :
                        idx === 1 ? 'bg-gray-400 text-black' :
                        idx === 2 ? 'bg-orange-600 text-white' :
                        'bg-neutral-700 text-gray-300'
                      }`}>
                        {driver.position}
                      </div>
                    </td>
                    
                    {/* Driver Number */}
                    <td className="py-2 px-3 text-gray-400 font-mono">{driver.driver_number}</td>
                    
                    {/* Driver Name */}
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        {driver.is_favorite && (
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                        )}
                        <span className={`truncate max-w-[150px] ${
                          driver.is_favorite ? 'text-yellow-300 font-medium' : 'text-white'
                        }`}>
                          {driver.driver_name}
                        </span>
                      </div>
                    </td>
                    
                    {/* Best Lap */}
                    <td className={`py-2 px-3 text-right font-mono font-bold ${
                      idx === 0 ? 'text-green-400' : 'text-white'
                    }`}>
                      {formatTime(driver.best_lap)}
                    </td>
                    
                    {/* Gap */}
                    <td className="py-2 px-3 text-right font-mono text-red-400">
                      {driver.gap > 0 ? `+${driver.gap.toFixed(3)}` : '-'}
                    </td>
                    
                    {/* Sector Times */}
                    {Array.from({ length: numSectors }, (_, i) => (
                      <td key={i} className="py-2 px-2 text-right font-mono text-gray-400 text-xs">
                        {driver.best_sectors[i] ? formatSectorTime(driver.best_sectors[i]) : '--'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Sector Rankings */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {bestTimes.best_sectors.map((sector) => {
          const ranking = getSectorRanking(sector.sector_number);
          const isExpanded = expandedSectors[sector.sector_number];
          const hasFavoriteInRanking = ranking.some(d => d.is_favorite);
          
          return (
            <Card
              key={sector.sector_number}
              className={`bg-neutral-900 border-red-900/30 hover:border-red-600 transition-all ${
                hasFavoriteInRanking ? 'ring-1 ring-yellow-500/30' : ''
              }`}
              data-testid={`best-sector-${sector.sector_number}`}
            >
              <CardContent className="p-4">
                {/* Sector Header */}
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSector(sector.sector_number)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-600 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-lg">S{sector.sector_number}</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold">Sector {sector.sector_number}</p>
                      <p className="text-xs text-gray-500">{ranking.length} pilotos</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-400">{formatTime(sector.best_time)}</p>
                      <p className="text-xs text-gray-500">#{sector.driver_number}</p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </div>

                {/* Ranking List */}
                {isExpanded && ranking.length > 0 && (
                  <div className="mt-4 space-y-1 max-h-64 overflow-y-auto">
                    {ranking.map((driver, idx) => (
                      <div
                        key={driver.driver_number}
                        className={`flex items-center justify-between p-2 rounded transition-colors ${
                          driver.is_favorite 
                            ? 'bg-yellow-500/10 border border-yellow-500/30' 
                            : idx === 0 
                              ? 'bg-green-500/10'
                              : 'hover:bg-neutral-800/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {/* Position Badge */}
                          <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-500 text-black' :
                            idx === 1 ? 'bg-gray-400 text-black' :
                            idx === 2 ? 'bg-orange-600 text-white' :
                            'bg-neutral-700 text-gray-300'
                          }`}>
                            {driver.position}
                          </div>
                          
                          {/* Favorite Star */}
                          {driver.is_favorite && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                          
                          {/* Driver Info */}
                          <div className="min-w-0">
                            <p className={`text-sm truncate ${
                              driver.is_favorite ? 'text-yellow-300 font-semibold' : 'text-gray-300'
                            }`}>
                              {driver.driver_name}
                            </p>
                            <p className="text-xs text-gray-500">#{driver.driver_number}</p>
                          </div>
                        </div>
                        
                        {/* Time & Gap */}
                        <div className="text-right">
                          <p className={`text-sm font-mono ${
                            idx === 0 ? 'text-green-400' : 'text-gray-300'
                          }`}>
                            {formatTime(driver.sector_time)}
                          </p>
                          {driver.gap > 0 && (
                            <p className="text-xs text-red-400">
                              +{driver.gap.toFixed(3)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Show favorite position hint if collapsed and has favorites */}
                {!isExpanded && hasFavoriteInRanking && (
                  <div className="mt-2 pt-2 border-t border-neutral-800">
                    {ranking.filter(d => d.is_favorite).map(fav => (
                      <div key={fav.driver_number} className="flex items-center gap-2 text-sm">
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                        <span className="text-yellow-300">{fav.driver_name}</span>
                        <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                          P{fav.position}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Legend */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-yellow-500 flex items-center justify-center text-black font-bold text-xs">1</div>
              <span className="text-gray-400">Líder del sector</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-gray-400">Piloto favorito</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-red-500" />
              <span className="text-gray-400">
                Vuelta teórica: suma de mejores sectores = {formatTime(bestTimes.theoretical_best_lap)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BestTimesView;
